import { Axios } from 'src/lib';

import { FronteggContext } from '@frontegg/rest-api';
import { showToastError } from 'src/utilities/errortoast';
import { showToastSuccess } from 'src/utilities/toast';
import { currentPractitioner, currentPractitionerLoading } from '../slices';
import {
  CSVUploadEnabledForCurrentUserSlice,
  settingsOrgFlagSlice,
} from '../slices/appointments.slice';
import {
  allPractitionerNamesLoadingSlice,
  allPractitionerNamesSlice,
  currentPractitionerEntitlements,
  currentPractitionerEntitlementsLoading,
  currentPractitionerSettings,
  currentPractitionerSettingsLoading,
  practitionerListLoading,
  practitionerListSlice,
  deletePractitionerLoading,
} from '../slices/practitioner.slice';

// Get details about the logged in practitioner
export const getCurrentPractitioner = (showLoading = true) => {
  return async (dispatch) => {
    if (showLoading) {
      dispatch(currentPractitionerLoading(true));
    }
    const headers = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${FronteggContext.getAccessToken()}`,
    };
    const result = await Axios.get(`/self`, { headers });
    const response = result?.data;
    if (result?.status === 200) {
      dispatch(currentPractitioner(response));
      dispatch(settingsOrgFlagSlice(response));
      dispatch(
        CSVUploadEnabledForCurrentUserSlice(
          response?.org_settings?.enable_bulk_appointment_upload ?? false
        )
      );
    } else {
      currentPractitioner({});
      showToastError('Failed to fetch details about logged in practitioner.');
    }
    if (showLoading) {
      dispatch(currentPractitionerLoading(false));
    }
  };
};

export const updateCurrentPractitioner = (data) => {
  return async (dispatch) => {
    const headers = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${FronteggContext.getAccessToken()}`,
    };
    const result = await Axios.put(`/self`, data, { headers });
    if (result?.status != 200) {
      showToastError('Failed to update practitioner.');
    } else {
      dispatch(getCurrentPractitioner(false));
    }
  };
};

export const getCurrentPractitionerSettings = () => {
  return async (dispatch) => {
    dispatch(currentPractitionerSettingsLoading(true));
    const headers = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${FronteggContext.getAccessToken()}`,
    };
    const result = await Axios.get(`/self/settings`, { headers });
    const response = result?.data;
    if (result?.status === 200) {
      dispatch(currentPractitionerSettings(response));
    } else {
      currentPractitioner({});
      showToastError('Failed to fetch practitioner settings.');
    }
    dispatch(currentPractitionerSettingsLoading(false));
  };
};

export const getCurrentPractitionerEntitlements = () => {
  return async (dispatch) => {
    dispatch(currentPractitionerEntitlementsLoading(true));
    const headers = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${FronteggContext.getAccessToken()}`,
    };
    const result = await Axios.get(`/self/entitlements`, { headers });
    const response = result?.data;
    if (result?.status === 200) {
      dispatch(currentPractitionerEntitlements(response));
    } else {
      currentPractitionerEntitlements({});
      showToastError('Failed to fetch practitioner entitlements.');
    }
    dispatch(currentPractitionerEntitlementsLoading(false));
  };
};

export const updateCurrentPractitionerSettings = (data) => {
  return async (dispatch) => {
    dispatch(currentPractitionerSettingsLoading(true));
    const headers = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${FronteggContext.getAccessToken()}`,
    };
    const result = await Axios.put(`/self/settings`, data, { headers });
    const response = result?.data;
    if (result?.status === 200) {
      dispatch(currentPractitionerSettings(response));
    } else {
      currentPractitionerSettings({});
      showToastError('Failed to update practitioner settings.');
    }
    dispatch(currentPractitionerSettingsLoading(false));
  };
};

export const listPractitioners = (include_roles = false) => {
  return async (dispatch) => {
    dispatch(practitionerListLoading(true));
    const headers = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${FronteggContext.getAccessToken()}`,
    };
    const params = include_roles ? { include_roles: true } : {};
    const result = await Axios.get(`/practitioners`, {
      headers,
      params,
    });
    const response = result?.data;
    if (result?.status === 200) {
      dispatch(practitionerListSlice(response?.practitioners));
    } else {
      practitionerListSlice([]);
      showToastError('Failed to fetch details about logged in practitioner.');
    }
    dispatch(practitionerListLoading(false));
  };
};

export const deletePractitioner = (practitionerId) => {
  return async (dispatch) => {
    dispatch(deletePractitionerLoading(true));
    const headers = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${FronteggContext.getAccessToken()}`,
    };

    try {
      const result = await Axios.delete(`/practitioner/${practitionerId}`, {
        headers,
      });
      if (result?.status === 200) {
        showToastSuccess('Practitioner deleted successfully.');
      } else {
        showToastError('Failed to delete practitioner.');
      }
    } catch (error) {
      showToastError('Failed to delete practitioner.');
    } finally {
      dispatch(deletePractitionerLoading(false));
    }
  };
};

export const listPractitionersFromOrg = (org_uuid, isSuperAdmin = true) => {
  return async (dispatch) => {
    dispatch(allPractitionerNamesLoadingSlice(true));
    const headers = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${FronteggContext.getAccessToken()}`,
      'Impersonate-Org-UUID': `${org_uuid}`,
      'Headless-Mode': true,
    };
    if (!isSuperAdmin) {
      delete headers['Impersonate-Org-UUID'];
      delete headers['Headless-Mode'];
    }
    const result = await Axios.get(
      isSuperAdmin ? `/practitioner/all` : `/scribe/practitioner/all`,
      { headers }
    );
    const response = result?.data;
    if (result?.status === 200) {
      dispatch(allPractitionerNamesSlice(response?.practitioners_data));
    } else {
      allPractitionerNamesSlice([]);
      showToastError('Failed to fetch details about logged in practitioner.');
    }
    dispatch(allPractitionerNamesLoadingSlice(false));
  };
};

export const updatePractitionerRoles = (
  practitionerId,
  roleNames,
  callback
) => {
  return async (dispatch) => {
    const headers = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${FronteggContext.getAccessToken()}`,
    };
    const payload = {
      practitioner_id: practitionerId,
      roles: roleNames,
    };
    try {
      const result = await Axios.put(
        `/v2/practitioners/change_roles`,
        payload,
        {
          headers,
        }
      );
      if (result?.status === 200) {
        showToastSuccess('Roles updated successfully');
        if (callback) {
          callback(true);
        }
        dispatch(listPractitioners(true));
      }
    } catch (error) {
      console.error('Error updating roles:', error);
      showToastError(`Failed to update roles`);
      if (callback) {
        callback(false);
      }
    }
  };
};
